import React, { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import { ActionButton } from '../components/ActionButton';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { OverwrIOSelect, OverwrIOTimeout } from './OverwrIOControl';
import AnalogOverwriteSetting from './AnalogOverwriteSetting';
import { analogOverwritePercent } from '../Utils/analog_overwrite';
import { LockSliderContext } from '../Contexts';

// Drafts belong to the page. Closing this overlay never saves or discards them.
export default function MultiIOOverwriteControl({
  controller,
  parent,
  slave,
  port,
  label,
  savedPort,
  dirty,
  anchor,
  onOpen,
  onClose,
  onChange,
  onSave,
  writable,
  connected,
  children
}) {
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width:600px)');
  const [showSaveInfo, setShowSaveInfo] = useState(false);
  const popoverAction = useRef(null);
  useEffect(() => {
    if (!anchor || mobile) return undefined;
    // Keep the overlay anchored when the page or a table container scrolls.
    const reposition = () => popoverAction.current?.updatePosition();
    window.addEventListener('scroll', reposition, true);
    return () => window.removeEventListener('scroll', reposition, true);
  }, [anchor, mobile]);
  const analog = Number(port.type_id) === 3;
  const mode = Number(port.overwr_select ?? 0);
  const timeout = port.overwr_timeout || '00:00:00';
  const hasTimeout = timeout !== '00:00:00';
  const state =
    mode === 2 && analog
      ? `${analogOverwritePercent(port.overwr_val_ana ?? 0)}%`
      : t(mode === 1 ? 'ov_on' : mode === 3 ? 'ov_off' : 'ov_auto');
  const id = `multi-io-overwrite-${slave}-${port.id}`;
  const title = `${t('slave_address_hl')} ${slave} · I/O ${label}`;
  const close = () => {
    setShowSaveInfo(false);
    onClose();
  };
  const editor = (
    <div style={{ padding: 16, width: 300, maxWidth: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Typography id={`${id}-title`} variant="subtitle1" style={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        <Tooltip title={t('close')}>
          <IconButton
            size="small"
            aria-label={t('close')}
            onClick={close}
            style={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      {port.memo && <Typography variant="body2">{port.memo}</Typography>}
      <OverwrIOSelect
        controller={controller}
        parent={parent}
        type={port.type_id}
        select={mode}
        disabled={!writable}
        onChange={(event) => onChange('overwr_select', Number(event.target.value))}
      />
      {analog && (
        <LockSliderContext.Provider value="off">
          <AnalogOverwriteSetting
            id={`multi_out_${slave}_${port.id}_overwr_val_ana`}
            show="overwr_io_val_ana"
            controller={controller}
            cur_value={port.overwr_val_ana ?? 0}
            controller_value={savedPort.overwr_val_ana ?? 0}
            min={0}
            max={100}
            unit=" %"
            disabled={!writable || mode === 0}
            controls={{
              setValue: (id, value) => {
                if (writable && mode !== 0) onChange('overwr_val_ana', value);
              },
              resetSetting: () => {
                if (writable && mode !== 0)
                  onChange('overwr_val_ana', savedPort.overwr_val_ana ?? 0);
              }
            }}
            valueBoxStyle={{ boxShadow: 'none', padding: '2px 4px', margin: '4px 0' }}
          />
        </LockSliderContext.Provider>
      )}
      <div style={{ marginTop: 4 }}>
        <OverwrIOTimeout
          controller={controller}
          parent={parent}
          timeout={timeout}
          disabled={!writable}
          onChange={(event, value) => {
            if (value instanceof Date && !Number.isNaN(value.getTime())) {
              onChange('overwr_timeout', value.toLocaleTimeString('de-DE'));
            }
          }}
        />
        {hasTimeout && (
          <Button
            size="small"
            disabled={!writable}
            onClick={() => onChange('overwr_timeout', '00:00:00')}
          >
            {t('multi_io_remove_timeout')}
          </Button>
        )}
      </div>
      {writable && (
        <>
          {showSaveInfo && (
            <Typography
              id={`${id}-save-info`}
              variant="caption"
              component="p"
              style={{ margin: '8px 0' }}
            >
              {t('multi_io_save_all_notice')}
            </Typography>
          )}
          <ActionButton
            color="primary"
            variant="contained"
            disabled={!connected}
            title={connected ? '' : t('disabled_action_waiting_for_connection')}
            onClick={() => {
              if (onSave() === true) close();
            }}
          >
            {t('multi_io_apply_save')}
          </ActionButton>
          <Tooltip title={showSaveInfo ? '' : t('multi_io_save_all_notice')} arrow>
            <IconButton
              size="small"
              aria-label={t('multi_io_save_all_notice')}
              aria-expanded={showSaveInfo}
              aria-controls={showSaveInfo ? `${id}-save-info` : undefined}
              onClick={() => setShowSaveInfo((open) => !open)}
              style={{ margin: '0 4px', opacity: 0.7 }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </div>
  );
  return (
    <>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <div style={{ minWidth: 0 }}>
          {children}
          {(mode > 0 || dirty) && (
            <Typography variant="caption" component="div" color="primary">
              {t('overwrite_by_io_hl')}: {state}
              {hasTimeout ? ` · ${timeout}` : ''}
              {dirty ? ` · ${t('multi_io_overwrite_unsaved')}` : ''}
            </Typography>
          )}
        </div>
        <Tooltip title={t('overwrite_by_io_hl')}>
          <IconButton
            size="small"
            color={mode > 0 ? 'primary' : 'default'}
            aria-label={`${t('overwrite_by_io_hl')}: ${title}`}
            aria-haspopup="dialog"
            aria-expanded={Boolean(anchor)}
            aria-controls={anchor ? id : undefined}
            onClick={(event) => onOpen(event.currentTarget)}
            style={{ flexShrink: 0 }}
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      </div>
      {anchor &&
        (mobile ? (
          <Dialog open onClose={close} disableScrollLock aria-labelledby={`${id}-title`} id={id}>
            {editor}
          </Dialog>
        ) : (
          <Popover
            open
            action={popoverAction}
            anchorEl={anchor}
            onClose={close}
            disableScrollLock
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ id, role: 'dialog', 'aria-labelledby': `${id}-title` }}
          >
            {editor}
          </Popover>
        ))}
    </>
  );
}
